import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CARD_DEFS, normalizeLayout, findSticker, stickerSrc, timeSlot, cachedLayout, cacheLayout } from './homeLayout';

const API = process.env.REACT_APP_BACKEND_URL;

// The homepage: a hub of your spaces, with stickers. The sticker at the top
// follows the time of day; tap it for a wiggle, some hearts, and the next one.
// Which cards show (their order and size) and which stickers go where are set
// in settings → stickers & cards. Built-in sticker images live in public/stickers/.

const greetingNow = () => {
  const h = new Date().getHours();
  if (h >= 22 || h < 5) return h < 5 ? 'still up?' : 'good evening';
  if (h < 11) return 'good morning';
  if (h < 17) return 'good afternoon';
  return 'good evening';
};

/* ── icons ── */
const Icon = ({ children, size = 21 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);
const BookIcon = () => (
  <Icon>
    <path d="M4 4.5A1.5 1.5 0 0 1 5.5 3H19v16H5.5A1.5 1.5 0 0 0 4 20.5z" />
    <path d="M4 17.5A1.5 1.5 0 0 1 5.5 16H19" />
    <path d="M9 7.5h6" />
  </Icon>
);
const CheckListIcon = () => (
  <Icon>
    <path d="M3.5 6.5 5 8l2.5-2.5" /><path d="M3.5 12.5 5 14l2.5-2.5" /><path d="M3.5 18.5 5 20l2.5-2.5" />
    <path d="M11 7h9.5" /><path d="M11 13h9.5" /><path d="M11 19h9.5" />
  </Icon>
);
const VideoIcon = () => (
  <Icon>
    <rect x="3" y="6" width="13" height="12" rx="2.5" />
    <path d="M16 10.5 20.5 8v8L16 13.5" />
  </Icon>
);
const PenIcon = () => (
  <Icon>
    <path d="M4 20h4L19 9a2.1 2.1 0 0 0-3-3L5 17z" />
    <path d="M14 7l3 3" />
    <path d="M13 20h7" />
  </Icon>
);
const TimerIcon = () => (
  <Icon>
    <circle cx="12" cy="13.5" r="7.5" />
    <path d="M12 9.5v4l2.5 2" />
    <path d="M9.5 3h5" />
  </Icon>
);

// "1h 20m", "45m"
const hm = (min) => {
  const h = Math.floor((min || 0) / 60);
  const m = (min || 0) % 60;
  if (!h) return `${m}m`;
  return m ? `${h}h ${m}m` : `${h}h`;
};

const HeartIcon = ({ size = 11 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{ verticalAlign: '-1px' }}>
    <path fill="#d9a3b8" d="M12 20.5s-7.5-4.6-9.3-9.2C1.4 7.9 3.6 4.5 7 4.5c2 0 3.6 1.1 5 3 1.4-1.9 3-3 5-3 3.4 0 5.6 3.4 4.3 6.8-1.8 4.6-9.3 9.2-9.3 9.2z" />
  </svg>
);

const CompanionsIcon = () => (
  <Icon>
    <circle cx="9" cy="8.5" r="3" /><path d="M3.5 19a5.5 5.5 0 0 1 11 0" />
    <circle cx="16.5" cy="9.5" r="2.4" /><path d="M15.5 14.2a4.6 4.6 0 0 1 5 4.8" />
  </Icon>
);

// "2h ago", "yesterday", "Sep 12"
const ago = (iso) => {
  if (!iso) return '';
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  if (hours < 48) return 'yesterday';
  return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });
};

// the live line under each card title; falls back to the card's usual subtitle
const liveLine = (path, sum) => {
  if (!sum) return null;
  if (path === '/novels') {
    const n = sum.novels || {};
    if (n.translating) return { text: `translating ${n.translating} chapter${n.translating === 1 ? '' : 's'}…` };
    if (n.reading?.length) return { text: `reading ${n.reading[0].title}` };
    return null;
  }
  if (path === '/writing') {
    const w = sum.writing;
    if (!w) return null;
    if (w.running) return { text: `writing “${w.running_title}”…` };
    if (w.latest_title) return { text: `last: ${w.latest_title}` };
    return null;
  }
  if (path === '/todos') {
    const t = sum.todos || {};
    if (t.overdue) return { text: `${t.overdue} overdue${t.due_today ? ` · ${t.due_today} today` : ''}`, warn: true };
    if (t.due_today) return { text: `${t.due_today} due today` };
    if (t.open) return { text: `${t.open} open` };
    return { text: 'all clear ✓' };
  }
  if (path === '/video') {
    const v = sum.video;
    if (!v) return null;
    if (v.rendering) return { text: 'rendering…' };
    if (v.status === 'failed') return { text: 'last one failed', warn: true };
    return { text: `last made ${ago(v.at)}` };
  }
  if (path === '/fanfiction') {
    const f = sum.fanfiction;
    if (!f) return null;
    if (f.reading_title) return { text: `reading “${f.reading_title}”${f.reading > 1 ? ` +${f.reading - 1}` : ''}` };
    if (f.total) return { text: `${f.total} fic${f.total === 1 ? '' : 's'}` };
    return null;
  }
  if (path === '/vault') {
    const v = sum.vault;
    if (!v) return null;
    if (!v.connected) return { text: 'not connected yet' };
    if (v.files) return { text: `${v.files} file${v.files === 1 ? '' : 's'} · ${bytesLabel(v.used_bytes)}` };
    return null;
  }
  if (path === '/companions/list') {
    const c = sum.companion;
    if (!c) return null;
    return { text: `${c.name.toLowerCase()}: “${c.text}” · ${ago(c.at)}` };
  }
  return null;
};

const QuillIcon = () => (
  <Icon>
    <path d="M20 4c-6 0-11 4.5-12.5 11L6 20" />
    <path d="M20 4c0 6-4 10.5-10.5 11.5" />
    <path d="M9.5 10.5h5" />
  </Icon>
);

const VaultIcon = () => (
  <Icon>
    <rect x="3.5" y="5" width="17" height="14" rx="2" />
    <circle cx="12" cy="12" r="3" />
    <path d="M12 9v-.01M15 12h.01M7 19v1.5M17 19v1.5" />
  </Icon>
);

const CARD_ICONS = {
  study: <TimerIcon />, novels: <BookIcon />, writing: <PenIcon />, todos: <CheckListIcon />,
  video: <VideoIcon />, companions: <CompanionsIcon />, fanfiction: <QuillIcon />, vault: <VaultIcon />,
};

// "1.2 GB" from bytes
const bytesLabel = (n) => {
  if (!n) return '0 MB';
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(1)} GB`;
  return `${Math.max(1, Math.round(n / 1024 ** 2))} MB`;
};

export default function Home() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [greeting] = useState(greetingNow);
  const [layout, setLayout] = useState(cachedLayout);
  const [heroKey, setHeroKey] = useState(() => { const l = cachedLayout(); return l.greeting[timeSlot()]; });
  const [wiggle, setWiggle] = useState(0);
  const [hearts, setHearts] = useState([]);
  const [peekUp, setPeekUp] = useState(false);
  const [summary, setSummary] = useState(null);
  const [note, setNote] = useState(null);
  const [weekly, setWeekly] = useState(null);       // his Sunday letter (Sundays only)
  const [noteState, setNoteState] = useState('loading');   // loading | ready | error | none
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const isAdmin = localStorage.getItem('is_admin') === 'true';
  const username = localStorage.getItem('username') || '';
  const dropdownRef = useRef(null);

  useEffect(() => {
    // signed out (or a search engine visiting): go to the login page instead
    // of showing the homepage
    if (!token) { navigate('/login', { replace: true }); return undefined; }
    // your picture for the top-right button (set it in settings)
    axios.get(`${API}/account?token=${token}`)
      .then(res => setAvatarUrl(res.data.avatar_url || null))
      .catch(() => {});
    // your cards and stickers (shown from this browser's copy straight away)
    axios.get(`${API}/home/layout?token=${token}`)
      .then(res => {
        const l = normalizeLayout(res.data.layout);
        setLayout(l);
        cacheLayout(l);
        setHeroKey(l.greeting[timeSlot()]);
      })
      .catch(() => {});
    // live card lines, jump-back-in, and this month's recap
    axios.get(`${API}/home/summary?token=${token}`)
      .then(res => setSummary(res.data))
      .catch(() => {});
    // today's note from him (written on the first visit of the day, so the
    // first load of a day takes a few seconds)
    axios.get(`${API}/home/weekly-note?token=${token}`)
      .then(res => setWeekly(res.data.note || null))
      .catch(() => {});
    axios.get(`${API}/home/note?token=${token}`)
      .then(res => { setNote(res.data.note); setNoteState(res.data.note ? 'ready' : 'none'); })
      .catch(() => setNoteState('error'));
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // signs this device out on the server too, so its login stops working
  const logout = () => {
    axios.post(`${API}/auth/logout`, { token }).catch(() => {}).finally(() => {
      localStorage.clear();
      navigate('/login');
    });
  };

  const anotherNote = () => {
    setNoteState('loading');
    axios.post(`${API}/home/note/new`, { token })
      .then(res => { setNote(res.data.note); setNoteState('ready'); })
      .catch(() => setNoteState(note ? 'ready' : 'error'));
  };

  // tap the big sticker: wiggle, a few hearts, then the next sticker
  const tapHero = () => {
    setWiggle(w => w + 1);
    const burst = Array.from({ length: 5 }, (_, i) => ({
      id: `${Date.now()}-${i}`,
      left: 30 + Math.random() * 40,
      delay: i * 70,
      drift: Math.round((Math.random() - 0.5) * 60),
    }));
    setHearts(h => [...h, ...burst]);
    setTimeout(() => setHearts(h => h.filter(x => !burst.includes(x))), 1400);
    setTimeout(() => {
      setHeroKey(k => rotation[(rotation.indexOf(k) + 1) % rotation.length]);
    }, 260);
  };

  // the tap rotation; the day's first sticker joins it even if it's not ticked
  const rotation = (() => {
    const ids = layout.stickers.filter(x => x.rotation).map(x => x.id);
    const first = layout.greeting[timeSlot()];
    return ids.length ? (ids.includes(first) || !first ? ids : [first, ...ids]) : [first || 'night'];
  })();
  const heroSticker = findSticker(layout, heroKey) || findSticker(layout, rotation[0]);
  const hero = { src: stickerSrc(heroSticker), caption: heroSticker ? heroSticker.caption : '' };
  const peekSticker = findSticker(layout, layout.peeker);
  const fallbackImg = (e) => { if (!e.currentTarget.src.endsWith('/stickers/night.webp')) e.currentTarget.src = '/stickers/night.webp'; };

  const s = {
    page: {
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: '#0e0b0f', fontFamily: "'DM Sans', sans-serif", fontWeight: 300,
      color: '#e8ddd0', display: 'flex', flexDirection: 'column', overflowY: 'hidden',
    },
    header: {
      padding: '20px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      borderBottom: '1px solid rgba(201,147,90,0.06)',
      background: 'linear-gradient(180deg, #120f18 0%, #0e0b0f 100%)', flexShrink: 0, zIndex: 50,
    },
    logo: {
      fontFamily: "'Cormorant Garamond', serif", fontSize: '22px', fontWeight: 300, fontStyle: 'italic',
      color: '#e8ddd0', letterSpacing: '0.04em',
    },
    logoSpan: { color: '#c9935a' },
    accountWrap: { position: 'relative' },
    accountImg: { width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' },
    accountBtn: {
      width: '34px', height: '34px', borderRadius: '50%', overflow: 'hidden', padding: 0,
      background: 'linear-gradient(135deg, #3a2a1a, #5a3d20)', border: '1px solid rgba(201,147,90,0.2)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
      fontSize: '14px', color: '#c9935a', fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic',
    },
    dropdown: {
      position: 'absolute', top: '42px', right: 0, background: '#1e1825',
      border: '1px solid rgba(201,147,90,0.15)', borderRadius: '14px', padding: '6px', minWidth: '180px',
      boxShadow: '0 8px 24px rgba(0,0,0,0.4)', zIndex: 100,
    },
    dropdownItem: {
      display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', borderRadius: '10px',
      fontSize: '13px', color: '#e8ddd0', cursor: 'pointer', fontWeight: 300, background: 'transparent',
      border: 'none', width: '100%', textAlign: 'left', fontFamily: "'DM Sans', sans-serif",
    },
    dropdownDivider: { height: '1px', background: 'rgba(201,147,90,0.08)', margin: '4px 0' },
    dropdownDanger: { color: '#c9935a' },
    // the whole width scrolls (so you can swipe on the sides on an iPad),
    // while the content itself stays the same 680px column in the middle
    body: {
      flex: 1, overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain',
      paddingTop: '20px', paddingBottom: '140px',
      paddingLeft: 'max(24px, calc((100% - 632px) / 2))', paddingRight: 'max(24px, calc((100% - 632px) / 2))',
      width: '100%', boxSizing: 'border-box',
    },
    hero: {
      display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '26px', minHeight: '190px',
    },
    heroText: { flex: 1, minWidth: 0 },
    greeting: {
      fontFamily: "'Cormorant Garamond', serif", fontSize: '32px', fontWeight: 300, fontStyle: 'italic',
      color: '#e8ddd0', lineHeight: 1.1,
    },
    caption: { fontSize: '13px', color: '#8a7466', marginTop: '8px', minHeight: '18px' },
    tapHint: { fontSize: '11px', color: '#4a3d30', marginTop: '10px' },
    heroBtn: {
      position: 'relative', width: '190px', maxWidth: '48vw', flexShrink: 0, background: 'none', border: 'none',
      padding: 0, cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
    },
    heroImg: { width: '100%', height: 'auto', display: 'block', filter: 'drop-shadow(0 8px 18px rgba(0,0,0,0.45))' },
    noteCard: {
      position: 'relative', background: 'linear-gradient(135deg, #221a26, #1b1520)',
      border: '1px solid rgba(201,147,90,0.18)', borderRadius: '18px', padding: '14px 16px 14px',
      marginBottom: '14px', display: 'flex', gap: '12px', alignItems: 'flex-start',
    },
    noteAvatar: {
      width: '36px', height: '36px', borderRadius: '50%', overflow: 'hidden', flexShrink: 0,
      background: 'linear-gradient(135deg, #3a2a1a, #5a3d20)', display: 'flex', alignItems: 'center',
      justifyContent: 'center', color: '#c9935a', fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic',
    },
    noteBody: { flex: 1, minWidth: 0 },
    noteFrom: { fontSize: '11px', color: '#8a7466', marginBottom: '4px', display: 'flex', gap: '8px', alignItems: 'center' },
    noteText: { fontSize: '14.5px', lineHeight: 1.6, color: '#e8ddd0', whiteSpace: 'pre-wrap' },
    noteMuted: { fontSize: '13px', color: '#6d5d4e', fontStyle: 'italic' },
    noteAgain: {
      marginLeft: 'auto', background: 'none', border: 'none', color: '#6d5d4e', cursor: 'pointer',
      fontSize: '11px', padding: 0, fontFamily: "'DM Sans', sans-serif",
    },
    jumpRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', marginBottom: '28px' },
    jump: {
      display: 'flex', alignItems: 'center', gap: '10px', background: 'transparent',
      border: '1px solid rgba(201,147,90,0.14)', borderRadius: '14px', padding: '10px 12px',
      cursor: 'pointer', textAlign: 'left', color: '#e8ddd0', fontFamily: "'DM Sans', sans-serif", minWidth: 0,
    },
    jumpIcon: {
      width: '30px', height: '30px', borderRadius: '50%', flexShrink: 0, overflow: 'hidden',
      background: 'rgba(201,147,90,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#c9935a', fontSize: '13px',
    },
    jumpText: { minWidth: 0, flex: 1 },
    jumpLabel: { fontSize: '10px', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#6d5d4e' },
    jumpMain: { fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    liveLine: { color: '#9a8676' },
    liveWarn: { color: '#e0b87a' },
    videoThumb: { width: '100%', height: '100%', objectFit: 'cover', borderRadius: '13px' },
    recap: {
      marginTop: '30px', background: '#17131b', border: '1px solid rgba(201,147,90,0.08)',
      borderRadius: '18px', padding: '16px 18px',
    },
    recapTitle: {
      fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic', fontSize: '19px', color: '#e8ddd0', marginBottom: '12px',
    },
    recapGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(96px, 1fr))', gap: '12px' },
    recapNum: {
      fontFamily: "'Cormorant Garamond', serif", fontSize: '28px', lineHeight: 1, color: '#c9935a',
      fontVariantNumeric: 'tabular-nums',
    },
    recapLabel: { fontSize: '11px', color: '#8a7466', marginTop: '4px', lineHeight: 1.4 },
    sectionTitle: {
      fontSize: '11px', letterSpacing: '0.16em', color: '#4a3d30', textTransform: 'uppercase', marginBottom: '16px',
    },
    spacesGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
    fullCard: { gridColumn: '1 / -1', minHeight: 'auto', flexDirection: 'row', alignItems: 'center', gap: '14px' },
    studyBar: { height: '5px', borderRadius: '3px', background: 'rgba(201,147,90,0.12)', marginTop: '8px', overflow: 'hidden' },
    studyFill: { height: '100%', borderRadius: '3px', background: 'linear-gradient(90deg, #8a5f35, #c9935a)' },
    studySticker: {
      position: 'absolute', right: '6px', bottom: '-4px', width: '92px', height: 'auto', pointerEvents: 'none',
      filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.5))',
    },
    spaceCard: {
      position: 'relative', background: '#1e1825', border: '1px solid rgba(201,147,90,0.15)',
      borderRadius: '18px', padding: '16px 16px 18px', display: 'flex', flexDirection: 'column', gap: '12px',
      cursor: 'pointer', boxSizing: 'border-box', WebkitUserSelect: 'none', userSelect: 'none', minHeight: '124px',
    },
    spaceIcon: {
      width: '42px', height: '42px', borderRadius: '13px', background: 'linear-gradient(135deg, #3a2a1a, #5a3d20)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#e8ddd0', flexShrink: 0,
      boxShadow: '0 0 0 2px rgba(201,147,90,0.1)',
    },
    spaceTitle: {
      fontFamily: "'Cormorant Garamond', serif", fontSize: '19px', fontWeight: 300, fontStyle: 'italic',
      color: '#e8ddd0', marginBottom: '3px',
    },
    spaceSub: { fontSize: '11.5px', color: '#4a3d30', fontWeight: 300, lineHeight: 1.5, paddingRight: '8px' },
    cardSticker: {
      position: 'absolute', top: '-14px', right: '-8px', width: '70px', height: 'auto', pointerEvents: 'none',
      filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.5))',
    },
    peeker: {
      position: 'fixed', right: '14px', bottom: 0, width: '104px', background: 'none', border: 'none', padding: 0,
      cursor: 'pointer', zIndex: 40, WebkitTapHighlightColor: 'transparent',
    },
    peekerImg: { width: '100%', height: 'auto', display: 'block' },
    bubble: {
      position: 'absolute', bottom: '100%', right: '46px', marginBottom: '2px', background: '#1e1825',
      border: '1px solid rgba(201,147,90,0.25)', borderRadius: '14px 14px 4px 14px', padding: '6px 12px',
      fontSize: '12px', color: '#e8ddd0', whiteSpace: 'nowrap',
    },
  };

  if (!token) return null;

  return (
    <div style={s.page}>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=DM+Sans:wght@300;400&display=swap" rel="stylesheet" />

      <style>{`
        .r-card { transition: border-color .18s ease, transform .12s ease; }
        .r-card:hover { border-color: rgba(201,147,90,0.32); }
        .r-card:active { transform: scale(0.985); }
        .r-card:focus-visible, .hero-btn:focus-visible, .peeker:focus-visible { outline: 2px solid #c9935a; outline-offset: 2px; }
        .card-sticker { transform: rotate(8deg); transition: transform .25s ease; }
        .r-card:hover .card-sticker { transform: rotate(-4deg) scale(1.08); }
        .hero-float { animation: floaty 4.5s ease-in-out infinite; }
        @keyframes floaty { 0%,100% { transform: translateY(0) rotate(-1.5deg); } 50% { transform: translateY(-6px) rotate(1.5deg); } }
        .wiggle { animation: wiggle .55s ease; }
        @keyframes wiggle { 0% { transform: rotate(0) scale(1); } 20% { transform: rotate(-9deg) scale(1.06); }
          40% { transform: rotate(7deg) scale(1.06); } 60% { transform: rotate(-4deg); } 80% { transform: rotate(2deg); } 100% { transform: rotate(0) scale(1); } }
        .sticker-in { animation: pop .35s ease; }
        @keyframes pop { from { opacity: 0; transform: scale(.85); } to { opacity: 1; transform: scale(1); } }
        .heart { position: absolute; bottom: 45%; font-size: 18px; color: #e0788f; pointer-events: none;
          animation: rise 1.2s ease-out forwards; }
        @keyframes rise { from { opacity: 0; transform: translate(0, 0) scale(.6); } 15% { opacity: 1; }
          to { opacity: 0; transform: translate(var(--drift), -110px) scale(1.1); } }
        .peeker { transform: translateY(34%); transition: transform .35s cubic-bezier(.3,1.4,.5,1); }
        .peeker:hover { transform: translateY(27%); }
        .peeker.up { transform: translateY(8%); }
        @media (prefers-reduced-motion: reduce) {
          .r-card, .card-sticker, .peeker { transition: none; }
          .hero-float, .wiggle, .sticker-in, .heart { animation: none; }
          .heart { display: none; }
        }
      `}</style>

      {/* HEADER */}
      <header style={s.header}>
        <div style={s.logo}>re<span style={s.logoSpan}>v</span>erie</div>
        <div style={s.accountWrap} ref={dropdownRef}>
          <div style={s.accountBtn} onClick={() => setDropdownOpen(!dropdownOpen)}>
            {avatarUrl
              ? <img src={avatarUrl} alt="you" style={s.accountImg} />
              : (username?.[0]?.toUpperCase() || '?')}
          </div>
          {dropdownOpen && (
            <div style={s.dropdown}>
              <button style={s.dropdownItem} onClick={() => { navigate('/settings'); setDropdownOpen(false); }}>
                settings
              </button>
              {isAdmin && (
                <button style={s.dropdownItem} onClick={() => { navigate('/security'); setDropdownOpen(false); }}>
                  security
                </button>
              )}
              <div style={s.dropdownDivider} />
              <button style={{ ...s.dropdownItem, ...s.dropdownDanger }} onClick={logout}>
                ← log out
              </button>
            </div>
          )}
        </div>
      </header>

      {/* BODY */}
      <div style={s.body}>
        {/* GREETING + STICKER */}
        <div style={s.hero}>
          <div style={s.heroText}>
            <div style={s.greeting}>{greeting}</div>
            <div style={s.caption}>{hero.caption}</div>
            <div style={s.tapHint}>tap us ♡</div>
          </div>
          <button className="hero-btn" style={s.heroBtn} onClick={tapHero} aria-label="next sticker">
            <div className="hero-float">
              <div key={wiggle} className={wiggle ? 'wiggle' : ''}>
                <img key={heroKey} className="sticker-in" src={hero.src} alt="" style={s.heroImg} onError={fallbackImg} />
              </div>
            </div>
            {hearts.map(h => (
              <span
                key={h.id} className="heart"
                style={{ left: `${h.left}%`, animationDelay: `${h.delay}ms`, '--drift': `${h.drift}px` }}
              >♥</span>
            ))}
          </button>
        </div>

        {/* TODAY'S NOTE FROM HIM */}
        {noteState !== 'none' && (
          <div style={s.noteCard}>
            <div style={s.noteAvatar}>
              {note?.companion?.avatar_url
                ? <img src={note.companion.avatar_url} alt="" style={s.accountImg} />
                : (note?.companion?.name?.[0] || '♡')}
            </div>
            <div style={s.noteBody}>
              <div style={s.noteFrom}>
                <span>{note ? `${note.companion.name.toLowerCase()} left you a note` : 'a note for you'}</span>
                <span style={{ display: 'inline-flex', gap: '10px', marginLeft: 'auto' }}>
                  {noteState === 'ready' && (
                    <button style={s.noteAgain} onClick={anotherNote} title="ask for a different note">↻ another</button>
                  )}
                  <button style={s.noteAgain} onClick={() => navigate('/notes')} title="every note he's left you">all notes →</button>
                </span>
              </div>
              {noteState === 'loading' && <div style={s.noteMuted}>writing you a note…</div>}
              {noteState === 'error' && <div style={s.noteMuted}>couldn't get today's note — try again later</div>}
              {noteState === 'ready' && note && (
                <div style={{ ...s.noteText, cursor: 'pointer' }} role="link" tabIndex={0}
                  onClick={() => navigate('/notes')} onKeyDown={e => e.key === 'Enter' && navigate('/notes')}>{note.content}</div>
              )}
            </div>
          </div>
        )}

        {/* SUNDAYS: HIS LETTER ABOUT YOUR WEEK (kept in notes from him) */}
        {weekly && (
          <div style={{ ...s.noteCard, cursor: 'pointer' }} role="link" tabIndex={0}
            onClick={() => navigate('/notes')} onKeyDown={e => e.key === 'Enter' && navigate('/notes')}>
            <div style={s.noteAvatar}>
              {weekly.companion?.avatar_url
                ? <img src={weekly.companion.avatar_url} alt="" style={s.accountImg} />
                : (weekly.companion?.name?.[0] || '♡')}
            </div>
            <div style={s.noteBody}>
              <div style={s.noteFrom}><span>{weekly.companion ? `${weekly.companion.name.toLowerCase()} wrote about your week` : 'about your week'}</span></div>
              <div style={s.noteText}>{weekly.content}</div>
            </div>
          </div>
        )}

        {/* JUMP BACK IN */}
        {(summary?.continue_reading || summary?.back_to_chat) && (
          <div style={s.jumpRow}>
            {summary.continue_reading && (
              <button
                className="r-card" style={s.jump}
                onClick={() => {
                  const c = summary.continue_reading;
                  navigate(c.chapter_id ? `/novels/${c.novel_id}/chapters/${c.chapter_id}` : `/novels/${c.novel_id}`);
                }}
              >
                <span style={s.jumpIcon}><BookIcon /></span>
                <span style={s.jumpText}>
                  <span style={{ ...s.jumpLabel, display: 'block' }}>continue reading</span>
                  <span style={{ ...s.jumpMain, display: 'block' }}>
                    {summary.continue_reading.chapter_title
                      ? `${summary.continue_reading.chapter_title} · ${summary.continue_reading.novel_title}`
                      : summary.continue_reading.novel_title}
                  </span>
                </span>
              </button>
            )}
            {summary.back_to_chat && (
              <button
                className="r-card" style={s.jump}
                onClick={() => {
                  const c = summary.back_to_chat;
                  navigate(c.is_call ? `/call/${c.companion_id}` : `/chat/${c.companion_id}/${c.thread_id}`);
                }}
              >
                <span style={s.jumpIcon}>
                  {summary.back_to_chat.avatar_url
                    ? <img src={summary.back_to_chat.avatar_url} alt="" style={s.accountImg} />
                    : summary.back_to_chat.companion_name?.[0]}
                </span>
                <span style={s.jumpText}>
                  <span style={{ ...s.jumpLabel, display: 'block' }}>back to {summary.back_to_chat.companion_name.toLowerCase()}</span>
                  <span style={{ ...s.jumpMain, display: 'block' }}>
                    {summary.back_to_chat.is_call ? 'your call' : summary.back_to_chat.thread_title} · {ago(summary.back_to_chat.at)}
                  </span>
                </span>
              </button>
            )}
          </div>
        )}

        <div style={s.sectionTitle}>your spaces</div>
        <div style={s.spacesGrid}>
          {layout.cards.filter(c => c.show).map(c => {
            const def = CARD_DEFS.find(d => d.key === c.key);
            if (!def) return null;
            const full = c.size === 'full';
            const st = findSticker(layout, layout.card_stickers[c.key]);
            const go = () => navigate(def.path);
            const live = c.key === 'study' ? null : liveLine(def.path, summary);
            return (
              <div
                key={c.key}
                className="r-card"
                style={{ ...s.spaceCard, ...(full ? s.fullCard : {}), ...(full && st ? { paddingRight: '96px' } : {}) }}
                role="button" tabIndex={0}
                onClick={go}
                onKeyDown={(e) => e.key === 'Enter' && go()}
              >
                {st && <img className="card-sticker" src={stickerSrc(st)} alt="" style={full ? s.studySticker : s.cardSticker} onError={fallbackImg} />}
                <div style={s.spaceIcon}>
                  {c.key === 'video' && summary?.video?.thumb_url
                    ? <img src={summary.video.thumb_url} alt="" style={s.videoThumb} />
                    : CARD_ICONS[c.key]}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={s.spaceTitle}>{def.title}</div>
                  {c.key === 'study' && summary?.study ? (
                    (summary.study.subjects || []).length > 1 ? (
                      // the subjects you picked in study settings, each against its own goal
                      <>
                        {summary.study.subjects.map(sj => (
                          <div key={sj.key} style={{ marginTop: '6px' }}>
                            <div style={{ ...s.spaceSub, ...s.liveLine, marginTop: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: sj.color, flexShrink: 0 }} />
                              {sj.name} · {hm(sj.today_minutes)} of {hm(sj.goal_minutes)}
                            </div>
                            <div style={{ ...s.studyBar, marginTop: '5px' }}>
                              <div style={{ ...s.studyFill, background: sj.color, width: `${Math.min(100, (sj.today_minutes / sj.goal_minutes) * 100)}%` }} />
                            </div>
                          </div>
                        ))}
                        {summary.study.total_minutes > 0 && (
                          <div style={{ ...s.spaceSub, ...s.liveLine, marginTop: '8px' }}><HeartIcon /> {hm(summary.study.total_minutes)} together</div>
                        )}
                      </>
                    ) : (() => {
                      const one = (summary.study.subjects || [])[0];
                      const today = one ? one.today_minutes : summary.study.today_minutes;
                      const goal = one ? one.goal_minutes : summary.study.goal_minutes;
                      return (
                        <>
                          <div style={{ ...s.spaceSub, ...s.liveLine }}>
                            {hm(today)} of {hm(goal)} today{one && one.key !== 'nursing' ? ` · ${one.name}` : ''}
                            {summary.study.total_minutes > 0 && <> · <HeartIcon /> {hm(summary.study.total_minutes)} together</>}
                          </div>
                          <div style={s.studyBar}>
                            <div style={{ ...s.studyFill, ...(one ? { background: one.color } : {}), width: `${Math.min(100, (today / goal) * 100)}%` }} />
                          </div>
                        </>
                      );
                    })()
                  ) : (
                    <div style={{ ...s.spaceSub, ...(live ? s.liveLine : {}), ...(live?.warn ? s.liveWarn : {}) }}>
                      {live ? live.text : def.sub}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {/* A dedicated entrance to our visual novel; kept separate from the
              user-configurable card layout so no homeLayout/backend changes are needed. */}
          <div
            className="r-card"
            style={{
              ...s.spaceCard, ...s.fullCard,
              border: '1px solid rgba(220, 151, 184, 0.32)',
              background: 'linear-gradient(115deg, #271b2b, #1e1825 75%)',
            }}
            role="button"
            tabIndex={0}
            aria-label="play puppy and pipsqueak visual novel"
            onClick={() => navigate('/visual-novel')}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/visual-novel'); } }}
          >
            <div style={{
              ...s.spaceIcon,
              background: 'linear-gradient(135deg, #583451, #8d527e)',
              color: '#ffe1ee', fontSize: '24px',
            }} aria-hidden="true">♡</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={s.spaceTitle}>puppy &amp; pipsqueak</div>
              <div style={{ ...s.spaceSub, color: '#b99aaa' }}>
                our little visual novel · tap to play ♡
              </div>
            </div>
            <span style={{ color: '#c993a9', fontSize: '20px' }} aria-hidden="true">→</span>
          </div>
        </div>

        {/* THIS MONTH */}
        {summary?.recap && (() => {
          const r = summary.recap;
          const items = [
            [r.study_minutes ? hm(r.study_minutes) : 0, 'studied together'],
            [r.days_talked, r.days_talked === 1 ? 'day you talked' : 'days you talked'],
            [r.chapters_translated, r.chapters_translated === 1 ? 'chapter translated' : 'chapters translated'],
            [r.novels_finished, r.novels_finished === 1 ? 'novel finished' : 'novels finished'],
            [r.notes_written, r.notes_written === 1 ? 'reading note' : 'reading notes'],
            [r.todos_done, r.todos_done === 1 ? 'to-do done' : 'to-dos done'],
            [r.videos_made, r.videos_made === 1 ? 'video made' : 'videos made'],
          ].filter(([n]) => (typeof n === 'string' ? n.length > 0 : n > 0));
          if (items.length === 0) return null;
          return (
            <div style={s.recap}>
              <div style={s.recapTitle}>{r.month.toLowerCase()} so far</div>
              <div style={s.recapGrid}>
                {items.map(([n, label]) => (
                  <div key={label}>
                    <div style={s.recapNum}>{n}</div>
                    <div style={s.recapLabel}>{label}</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
      </div>

      {/* PEEKING FROM THE BOTTOM CORNER */}
      {peekSticker && <button
        className={`peeker${peekUp ? ' up' : ''}`}
        style={s.peeker}
        onClick={() => { setPeekUp(true); setTimeout(() => setPeekUp(false), 2200); }}
        aria-label="say hi"
      >
        {peekUp && <span style={s.bubble}>hi ♡</span>}
        <img src={stickerSrc(peekSticker)} alt="" style={s.peekerImg} onError={fallbackImg} />
      </button>}
    </div>
  );
}
